import { inject } from 'aurelia';
import { IHttpClient, json } from '@aurelia/fetch-client';
import { jwtDecode } from 'jwt-decode';
import { JwtPayloadExtended } from './models/JwtPayloadExtended';

@inject(IHttpClient)
export class TokenService {
    private TOKEN_KEY = 'jwt_token';
    private isRefreshing: boolean;
    private refreshTokenPromise: Promise<string> | null;

    constructor(private http: IHttpClient) {}

    private getStoredToken() {
        return window.localStorage[this.TOKEN_KEY] as string | null;
    }

    refreshAccessToken() {
        if (this.isRefreshing) {
            // If a refresh is already in progress, wait for it to complete
            return this.refreshTokenPromise;
        }

        this.isRefreshing = true;

        // Create a promise to manage the refresh state
        this.refreshTokenPromise = new Promise(async(resolve, reject) => {
            const token = this.getStoredToken();

            if (!token) {
                reject('No access token to refresh');
            }

            try {
                const response = await this.http.fetch('RefreshToken', {
                    method: 'POST',
                    body: json({ token }),
                    headers: {
                        'X-Skip-Interceptor': 'true'
                    }
                });

                if (!response.ok) {
                    this.isRefreshing = false;
                    this.refreshTokenPromise = null;
                    reject('Failed to refresh token');

                    return;
                }

                const data = await response.json();
                this.saveToken(data.token);

                this.isRefreshing = false;
                this.refreshTokenPromise = null;

                resolve(data.token);
            } catch (err) {
                this.isRefreshing = false;
                this.refreshTokenPromise = null;

                reject(err);
            }
        });

        return this.refreshTokenPromise;
    }

    saveToken(token: string | null) {
        if (!token) return;
        window.localStorage[this.TOKEN_KEY] = token;
    }

    async getToken() {
        const token = this.getStoredToken();
        const isValid = await this.isTokenValid(false, token, false);

        if (!isValid && token) {
            try {
                return await this.refreshAccessToken();
            } catch (e) {
                console.error(e);
                this.destroyToken();
                return null;
            }
        }

        return token;
    }

    async isTokenValid(ignoreExpire = false, token: string | null = null, fetchToken = true) {
        if (fetchToken) {
            token ??= await this.getToken();
        }

        if (!token) {
            return false;
        }

        let expired = false;
        let requiresIpAuthorization = false;

        try {
            const validToken = jwtDecode(token) as JwtPayloadExtended;
            expired = Date.now() >= (validToken.exp! * 1000);
            requiresIpAuthorization = Boolean(validToken?.ria);
        } catch {
            return false;
        }

        return token !== 'undefined' && token !== 'null' && (!expired || ignoreExpire) && !requiresIpAuthorization;
    }

    destroyToken() {
        window.localStorage.removeItem(this.TOKEN_KEY);
    }
}
